// src/app/components/searshForms/searchForm/SearchForm.js
import React, { useEffect, useState, useCallback, useRef } from "react";
import { Button, Calendar, SearchSelect, SearchSummary } from "@zorni/components-lib";
import useReactSimpleMatchMedia from "react-simple-matchmedia";
import { useTranslation } from "react-i18next";
import styles from "./SearchForm.module.css";
import { useNavigate } from "react-router-dom";
import TravelersPicker from "../../../../common/travelersPicker/TravelersPicker";
import { useSearch } from "../../../context/SearchContext";
import { useSearch as useSharedSearch } from "../../../hooks/SearchContext";
import { useDestinationSearchSelect } from "../../../hooks/useDestinationSearchSelect";

// ===== Helpers dates (local, sans UTC / toISOString) =====
const formatLocalDate = (d) => {
  if (!d || !(d instanceof Date) || Number.isNaN(d.getTime())) return "";
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`; // AAAA-MM-JJ
};

const toLocalDateFromAny = (val) => {
  if (!val) return null;
  if (val instanceof Date) {
    return Number.isNaN(val.getTime()) ? null : val;
  }

  if (typeof val === "string") {
    // on tente d'abord AAAA-MM-JJ
    const parts = val.split("-");
    if (parts.length === 3) {
      const [y, m, d] = parts.map(Number);
      if (y && m && d) {
        const date = new Date(y, m - 1, d);
        return Number.isNaN(date.getTime()) ? null : date;
      }
    }
    // fallback générique (new Date gère d’autres formats)
    const d2 = new Date(val);
    return Number.isNaN(d2.getTime()) ? null : d2;
  }

  const d3 = new Date(val);
  return Number.isNaN(d3.getTime()) ? null : d3;
};

/**
 * variant
 *   - "card"   : carte posée à cheval sur le hero de l'accueil (défaut)
 *   - "header" : recherche dépliée dans l'en-tête. Pas de défilement de la
 *                page à l'ouverture des popovers : l'en-tête est collant.
 * autoOpenField / autoOpenKey : ouvre directement « dest », « dates » ou
 *   « pax » ; autoOpenKey change à chaque demande, pour rouvrir le même champ.
 * onSearch : appelé après la navigation vers les résultats.
 */
const SearchForm = ({
  value,
  onChange,
  variant = "card",
  autoOpenField,
  autoOpenKey,
  onSearch,
}) => {
  const isHeader = variant === "header";
  const rootRef = useRef(null);
  const navigate = useNavigate();
  const { t } = useTranslation();
  // Téléphone uniquement : les tablettes utilisent la grille SearchForm complète.
  const isMobile = useReactSimpleMatchMedia("(max-width: 599.98px)");
  // Tablettes : les trois champs partagent une ligne, invite de destination courte.
  const isTablet = useReactSimpleMatchMedia("(max-width: 1279.98px)");

  // Contexte global (peut être undefined si pas de provider)
  const ctx = useSearch();
  // Feuille de recherche mobile (MobileSearchSheet), ouverte depuis le récapitulatif.
  const { setSheetOpen } = useSharedSearch();
  const hasCtx = ctx && ctx.updateSearch;

  // Si on a des props, on les utilise
  const fromProps = !!value && !!onChange;

  // ====== 1) SOURCE DE VÉRITÉ ======
  const [local, setLocal] = useState({
    destination: "",
    startDate: "",
    endDate: "",
    adults: 0,
    children: 0,
    babies: 0,
  });

  const current = fromProps
    ? value
    : hasCtx
    ? {
        destination: ctx.search.destination || "",
        startDate: ctx.search.startDate || "",
        endDate: ctx.search.endDate || "",
        adults: ctx.search.adults || 0,
        children: ctx.search.children || 0,
        babies: ctx.search.babies || 0,
      }
    : local;

  // 🔧 On reconstruit le range sous forme de Date (local)
  const currentRange = [
    toLocalDateFromAny(current.startDate),
    toLocalDateFromAny(current.endDate),
  ];

  // ====== 2) FONCTION POUR MODIFIER ======
  const setField = (patch) => {
    if (fromProps) {
      onChange(patch);
    } else if (hasCtx) {
      ctx.updateSearch(patch);
    } else {
      setLocal((prev) => ({ ...prev, ...patch }));
    }
  };

  // ====== 3) ETATS ANNEXES UI ======
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showVoyageur, setShowVoyageur] = useState(false);
  const dateFieldRef = useRef(null);
  const travelerFieldRef = useRef(null);

  const [destinationQuery, setDestinationQuery] = useState(current.destination || "");
  const {
    isCityLoading,
    hasCityRequested,
    recentOptions,
    lookupDestinationOptions,
    shouldHideDestinationPopover,
    handleDestinationInput,
    handleDestinationChange,
    handleSelectRecent,
    addRecentSearch,
    buildRecentDateLabel,
  } = useDestinationSearchSelect({
    destination: current.destination,
    setDestination: (next) => setField({ destination: next }),
    destinationQuery,
    setDestinationQuery,
    onApplyRecent: (opt) => {
      setField({
        destination: opt.value || opt.label || "",
        startDate: opt.startDate || "",
        endDate: opt.endDate || "",
        adults: opt.travelers?.adult || 0,
        children: opt.travelers?.child || 0,
        babies: opt.travelers?.baby || 0,
      });
      // Pas de navigation : la recherche ne part qu'au clic sur « Rechercher ».
    },
  });

  const handleDateChange = (range) => {
    const [from, to] = Array.isArray(range) ? range : [null, null];

    setField({
      startDate: from ? formatLocalDate(from) : "",
      endDate: to ? formatLocalDate(to) : "",
    });
  };

  const ensureSpaceBelow = useCallback((anchor, estimatedHeight) => {
    if (!anchor || isHeader) return 0;

    const rect = anchor.getBoundingClientRect();
    const viewportBottom = window.innerHeight;
    const overflow = rect.bottom + estimatedHeight - viewportBottom;

    if (overflow > 0) {
      window.scrollBy({
        top: overflow + 24,
        behavior: "smooth",
      });
      return 260;
    }

    return 0;
  }, [isHeader]);

  const handleCalendarBeforeOpen = useCallback(() => {
    return ensureSpaceBelow(dateFieldRef.current, 420);
  }, [ensureSpaceBelow]);

  const handleTravelerBeforeOpen = useCallback(() => {
    return ensureSpaceBelow(travelerFieldRef.current, 340);
  }, [ensureSpaceBelow]);

  const handleClick = () => {
    if ((current.destination || "").trim()) {
      addRecentSearch({
        dateRange: buildRecentDateLabel(currentRange),
        label: current.destination || destinationQuery,
        value: current.destination || destinationQuery,
        startDate: current.startDate || "",
        endDate: current.endDate || "",
        travelers: {
          adult: current.adults,
          child: current.children,
          baby: current.babies,
        },
      });
    }

    const params = {
      destination: current.destination || null,
      startDate: current.startDate || null,
      endDate: current.endDate || null,
      adult: current.adults || null,
      child: current.children || null,
      baby: current.babies || null,
    };

    // Filtrer les champs valides (pas null, pas "", pas 0)
    const queryString = Object.entries(params)
      .filter(([_, v]) => v !== null && v !== "" && v !== 0)
      .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
      .join("&");

    navigate(`/trips${queryString ? `?${queryString}` : ""}`);
    onSearch?.();
  };

  // Ouverture directe d'un champ (recherche dépliée depuis l'en-tête).
  useEffect(() => {
    if (!autoOpenField) return undefined;
    const root = rootRef.current;
    // Laisse le temps au panneau de l'en-tête de s'afficher : le popover se
    // positionne sur la taille réelle du champ.
    const timer = window.setTimeout(() => {
      if (!root) return;
      if (autoOpenField === "dest") {
        root.querySelector('input[role="combobox"]')?.focus();
      } else if (autoOpenField === "dates") {
        setShowVoyageur(false);
        dateFieldRef.current?.querySelector('[role="button"]')?.click();
      } else if (autoOpenField === "pax") {
        setShowVoyageur(true);
      }
    }, 80);
    return () => window.clearTimeout(timer);
  }, [autoOpenField, autoOpenKey]);


  // --- Téléphone : récapitulatif qui ouvre la feuille de recherche ---
  if (isMobile) {
    return <SearchSummary value={current} onClick={() => setSheetOpen(true)} t={t} />;
  }

  // --- Desktop / Hero ---
  return (
    <div
      ref={rootRef}
      className={`${styles.bar} ${isHeader ? styles.barHeader : ""}`}
    >
      {/* Destination */}
      <div className={styles.field}>
        <SearchSelect t={t}
          appearance="inline"
          label={t("search.destination", "Destination")}
          placeholder={
            isTablet && !isHeader
              ? t("search.destinationPlaceholderShort", "Où partir ?")
              : t("search.destinationPlaceholder", "Où souhaitez-vous partir ?")
          }
          iconLeft="iconLocation"
          inputSize="medium"
          id="destination"
          value={current.destination}
          options={lookupDestinationOptions}
          recentOptions={recentOptions}
          onSelectOption={handleSelectRecent}
          isLoading={isCityLoading}
          className={
            shouldHideDestinationPopover
              ? styles.destinationSelectNoResults
              : undefined
          }
          onInput={handleDestinationInput}
          onChange={handleDestinationChange}
        />
      </div>

      {/* Dates */}
      <div className={styles.field} ref={dateFieldRef}>
        <Calendar
          withInput
          appearance="inline"
          floatingLabel={false}
          iconName="iconCalendar"
          iconPosition="left"
          inputLabel={t("search.period", "Période")}
          inputSize="medium"
          type="voyage"
          value={currentRange}
          onChange={handleDateChange}
          showEmptyValue
          placeholder={
            isTablet && !isHeader ? t("search.datesPlaceholderShort", "Ajouter des dates") : undefined
          }
          beforeOpen={handleCalendarBeforeOpen}
        />
      </div>

      {/* Voyageurs */}
      <div className={styles.field} ref={travelerFieldRef}>
        <TravelersPicker
          isFilter={false}
          appearance="inline"
          floatingLabel={false}
          clearable
          adult={current.adults}
          child={current.children}
          baby={current.babies}
          setAdult={(v) => setField({ adults: v })}
          setChild={(v) => setField({ children: v })}
          setBaby={(v) => setField({ babies: v })}
          showVoyageur={showVoyageur}
          setShowVoyageur={setShowVoyageur}
          setShowDatePicker={setShowDatePicker}
          showEmptyPreview
          placeholder={
            isTablet && !isHeader ? t("search.travelersPlaceholderShort", "Voyageurs") : undefined
          }
          beforeOpen={handleTravelerBeforeOpen}
        />
      </div>

      {/* CTA */}
      <div className={styles.cta}>
        <Button
          iconName="iconSearch"
          iconPosition="left"
          label={t("search.submit", "Rechercher")}
          size="large"
          variant="primary"
          onClick={handleClick}
        />
      </div>
    </div>
  );
};


export default SearchForm;
